// Vault — crypto wallet home.
//
// Two views within one route:
//   /vault               → currency list with aggregate total
//   /vault?currency=TRX_USDT → per-currency detail (balance + receive QR)
//
// Navigation from detail:
//   Send    → /vault/send?currency=TRX_USDT
//   History → /vault/history?currency=TRX_USDT
//   Back    → /vault (list)
//   Back from list → / (card dashboard)

import { useQuery } from '@tanstack/react-query';
import { createRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, type ReactNode } from 'react';

import {
  getBotInfo,
  getWalletAddress,
  getWalletBalances,
  listCurrencies,
  type WalletBalance,
} from '../api/client.ts';
import {
  IconArrowDown,
  IconArrowUp,
  IconChevronLeft,
  IconCopy,
  IconList,
} from '../components/icons.tsx';
import { ErrorState } from '../components/states.tsx';
import { showToast } from '../components/toast.tsx';
import { applyPendingSends } from '../lib/pending-sends.ts';
import { cardCurrencyToVault } from '../lib/vault-currency.ts';

import { rootRoute } from './__root.tsx';

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Derives display metadata from a currency code such as "TRX_USDT".
const currencyMeta = (
  currency: string,
): {
  readonly name: string;
  readonly network: string;
  readonly token: string;
  readonly colorClass: string;
} => {
  const map: Record<string, { name: string; network: string; token: string; colorClass: string }> =
    {
      TRX: { name: 'Tron', network: 'Tron', token: 'TRX', colorClass: '' },
      TRX_USDT: {
        name: 'Tether USD',
        network: 'Tron · TRC-20',
        token: 'USDT',
        colorClass: 'usdt',
      },
      SOL_USDT: {
        name: 'Tether USD',
        network: 'Solana · SPL',
        token: 'USDT',
        colorClass: 'usdt',
      },
      ETH_USDT: {
        name: 'Tether USD',
        network: 'Ethereum · ERC-20',
        token: 'USDT',
        colorClass: 'usdt',
      },
      ETH_USDC: {
        name: 'USD Coin',
        network: 'Ethereum · ERC-20',
        token: 'USDC',
        colorClass: 'usdc',
      },
      SOL_USDC: {
        name: 'USD Coin',
        network: 'Solana · SPL',
        token: 'USDC',
        colorClass: 'usdc',
      },
    };
  return (
    map[currency] ?? {
      name: currency,
      network: '—',
      token: currency.split('_')[1] ?? currency,
      colorClass: '',
    }
  );
};

// Formats a raw decimal balance string for display.
// Shows 2 dp minimum; up to 6 dp when meaningful sub-cent precision exists.
const formatBalance = (raw: string | undefined): { readonly int: string; readonly dec: string } => {
  if (raw === undefined || raw === '') return { int: '0', dec: '.00' };
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return { int: '0', dec: '.00' };
  const decLen = raw.includes('.') ? (raw.split('.')[1]?.replace(/0+$/, '').length ?? 0) : 0;
  const dp = Math.max(2, Math.min(6, decLen));
  const [intStr = '0', decStr = '00'] = n.toFixed(dp).split('.');
  return { int: Number(intStr).toLocaleString('en-US'), dec: `.${decStr}` };
};

const copyToClipboard = async (text: string): Promise<boolean> => {
  if (typeof navigator === 'undefined' || navigator.clipboard === undefined) return false;
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

// ─── View 1: Currency list ────────────────────────────────────────────────────

const CurrencyListView = ({
  balances,
  onSelect,
}: {
  readonly balances: readonly WalletBalance[];
  readonly onSelect: (currency: string) => void;
}): ReactNode => {
  // Aggregate total in USD
  const totalUsd = balances.reduce((acc, b) => {
    const v = Number(b.denominated?.USD ?? '0');
    return acc + (Number.isFinite(v) ? v : 0);
  }, 0);
  const [ti = '0', td = '00'] = totalUsd.toFixed(2).split('.');

  return (
    <>
      <div className="vault-total fade-in d1">
        <p className="eyebrow hero-eyebrow">Total Balance</p>
        <div className="hero-amount">
          <span className="hero-symbol">$</span>
          <span className="hero-int">{Number(ti).toLocaleString('en-US')}</span>
          <span className="hero-dec">.{td}</span>
        </div>
      </div>

      <div className="hairline fade-in d2" />

      <div className="fade-in d3">
        {balances.length === 0 ? (
          <div className="empty-state">No wallets available</div>
        ) : (
          balances.map((b, i) => {
            const meta = currencyMeta(b.currency);
            const bal = formatBalance(b.amount);
            const usdValue = b.denominated?.USD;
            const usd =
              usdValue !== undefined && Number(usdValue) > 0
                ? `$${Number(usdValue).toFixed(2)}`
                : null;
            return (
              <div key={b.currency}>
                <button
                  type="button"
                  className="currency-row"
                  onClick={(): void => {
                    onSelect(b.currency);
                  }}
                >
                  <div className={`wallet-ccy-icon ${meta.colorClass}`} aria-hidden="true">
                    {meta.token}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p className="currency-name">{meta.name}</p>
                    <p className="currency-network">{meta.network}</p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p
                      className="currency-min"
                      style={{ color: 'var(--text-primary)', fontSize: 13 }}
                    >
                      {bal.int}
                      {bal.dec}
                    </p>
                    {usd !== null ? (
                      <p className="currency-network" style={{ color: 'var(--text-tertiary)' }}>
                        {usd}
                      </p>
                    ) : null}
                  </div>
                  <span
                    style={{
                      marginLeft: 10,
                      color: 'var(--text-secondary)',
                      fontSize: 20,
                      lineHeight: 1,
                    }}
                    aria-hidden="true"
                  >
                    ›
                  </span>
                </button>
                {i < balances.length - 1 ? <div className="hairline" /> : null}
              </div>
            );
          })
        )}
      </div>
    </>
  );
};

// ─── View 2: Per-currency detail (balance + receive QR) ──────────────────────

const CurrencyDetailView = ({
  currency,
  balance,
  onSend,
  onHistory,
  onTopUpCard,
  canTopUpCard,
}: {
  readonly currency: string;
  readonly balance: WalletBalance | undefined;
  readonly onSend: () => void;
  readonly onHistory: () => void;
  readonly onTopUpCard: () => void;
  // Only true when a card top-up can accept this vault currency; otherwise the
  // shortcut is hidden to avoid navigating to a dead end.
  readonly canTopUpCard: boolean;
}): ReactNode => {
  const meta = currencyMeta(currency);
  const bal = formatBalance(balance?.amount);

  const addressQ = useQuery({
    queryKey: ['wallet-address', currency],
    queryFn: ({ signal }) => getWalletAddress(currency, signal),
    // Addresses are stable once created — cache for 10 minutes.
    staleTime: 10 * 60 * 1000,
  });

  const onCopy = (): void => {
    const addr = addressQ.data?.address;
    if (addr === undefined) return;
    void copyToClipboard(addr).then((ok) => {
      showToast(ok ? 'Address copied' : 'Copy failed');
    });
  };

  return (
    <>
      {/* Balance hero */}
      <div className="hero fade-in d1" style={{ paddingBottom: 24 }}>
        <p className="eyebrow hero-eyebrow">
          {meta.name} · {meta.network}
        </p>
        <div className="hero-amount">
          <span className="hero-int">{bal.int}</span>
          <span className="hero-dec">{bal.dec}</span>
        </div>
        <p className="hero-meta">{meta.token}</p>
      </div>

      {/* Actions */}
      <div className="action-row fade-in d2">
        {/* "Receive" scrolls to the QR — the deposit surface is below */}
        <button
          type="button"
          className="btn-ghost"
          onClick={(): void => {
            document
              .getElementById('wallet-deposit-section')
              ?.scrollIntoView({ behavior: 'smooth' });
          }}
        >
          <IconArrowDown /> Receive
        </button>
        <button type="button" className="btn-ghost" onClick={onSend}>
          <IconArrowUp /> Send
        </button>
        <button type="button" className="btn-ghost" onClick={onHistory}>
          <IconList /> History
        </button>
      </div>

      {canTopUpCard ? (
        <div className="action-row-secondary fade-in">
          <button type="button" className="btn-ghost" onClick={onTopUpCard}>
            Top up card
          </button>
        </div>
      ) : null}

      {/* Deposit address + QR */}
      <div id="wallet-deposit-section" className="fade-in d3">
        <div className="section-header" style={{ marginTop: 28 }}>
          <p className="eyebrow">Deposit address</p>
        </div>
        <div className="hairline" style={{ marginTop: 8, marginBottom: 18 }} />

        {addressQ.isLoading ? (
          <div className="qr-frame">
            <div className="loading-spinner" aria-hidden="true" />
          </div>
        ) : addressQ.isError ? (
          <div className="qr-frame">
            <div className="qr-frame__empty">Address unavailable</div>
          </div>
        ) : addressQ.data !== undefined ? (
          <>
            <div className="qr-frame">
              {addressQ.data.qrCode !== undefined && addressQ.data.qrCode !== '' ? (
                <img src={addressQ.data.qrCode} alt={`QR code for ${meta.token} deposit address`} />
              ) : (
                <div className="qr-frame__empty">
                  Scan the address below
                  <br />
                  to deposit {meta.token}
                </div>
              )}
            </div>

            <div className="address-frame">
              <div className="address-row">
                <p className="address-text" title={addressQ.data.address}>
                  <span aria-hidden="true">{addressQ.data.address}</span>
                  <span className="sr-only">{addressQ.data.address}</span>
                </p>
                <button
                  type="button"
                  className="address-copy"
                  aria-label="Copy address"
                  onClick={onCopy}
                >
                  <IconCopy />
                </button>
              </div>
            </div>

            <p
              role="note"
              style={{
                fontSize: 12,
                color: 'var(--text-tertiary)',
                textAlign: 'center',
                marginBottom: 24,
                lineHeight: 1.55,
              }}
            >
              Send only {meta.token} on the {meta.network.split(' ·')[0]} network to this address.
            </p>
          </>
        ) : null}
      </div>
    </>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

export const VaultPage = (): ReactNode => {
  const navigate = useNavigate();
  const { currency } = vaultRoute.useSearch();

  const balancesQ = useQuery({
    queryKey: ['wallet-balances'],
    queryFn: ({ signal }) => getWalletBalances(signal),
    staleTime: 30_000,
    select: applyPendingSends,
  });
  const currenciesQ = useQuery({
    queryKey: ['currencies'],
    queryFn: ({ signal }) => listCurrencies(signal),
  });

  // Vault currencies a card top-up can accept — i.e. some card-platform
  // currency maps onto them. Gates the "Top up card" shortcut so it never
  // navigates to a dead end for an unsupported currency.
  const topupCurrencies = new Set(
    (currenciesQ.data?.currencies ?? [])
      .map((c) => cardCurrencyToVault(c.symbol, c.network))
      .filter((v): v is string => v !== null),
  );

  // Direct navigation to /vault must not surface the wallet when the feature
  // is off. Bounce back to the dashboard once bot-info confirms it is disabled
  // (the endpoints 404 anyway, but redirecting avoids showing an error state).
  const botInfoQ = useQuery({
    queryKey: ['bot-info'],
    queryFn: ({ signal }) => getBotInfo(signal),
    staleTime: Infinity,
  });
  const walletDisabled = botInfoQ.data !== undefined && !botInfoQ.data.walletEnabled;
  useEffect(() => {
    if (walletDisabled) void navigate({ to: '/' });
  }, [walletDisabled, navigate]);
  if (walletDisabled) return null;

  const isDetail = currency !== undefined;
  const meta = currency !== undefined ? currencyMeta(currency) : null;
  const selectedBalance = balancesQ.data?.balances.find((b) => b.currency === currency);

  if (balancesQ.isError) {
    return (
      <ErrorState
        message="Could not load wallet"
        onRetry={(): void => {
          void balancesQ.refetch();
        }}
      />
    );
  }

  return (
    <main className="screen">
      <div className="topbar fade-in">
        <button
          type="button"
          className="topbar-back"
          aria-label="Back"
          onClick={(): void => {
            if (isDetail) {
              void navigate({ to: '/vault' });
            } else {
              void navigate({ to: '/' });
            }
          }}
        >
          <IconChevronLeft />
        </button>
        <p className="eyebrow">{isDetail && meta !== null ? meta.token : 'Vault'}</p>
        <span className="topbar-spacer" aria-hidden="true" />
      </div>

      {balancesQ.isLoading ? (
        <div style={{ textAlign: 'center', padding: '64px 0' }}>
          <div className="loading-spinner" style={{ margin: '0 auto' }} aria-hidden="true" />
        </div>
      ) : isDetail && currency !== undefined ? (
        <CurrencyDetailView
          currency={currency}
          balance={selectedBalance}
          onSend={(): void => {
            void navigate({ to: '/vault/send', search: { currency } });
          }}
          onHistory={(): void => {
            void navigate({ to: '/vault/history', search: { currency } });
          }}
          onTopUpCard={(): void => {
            void navigate({ to: '/topup/from-vault', search: { vaultCurrency: currency } });
          }}
          canTopUpCard={topupCurrencies.has(currency)}
        />
      ) : (
        <CurrencyListView
          balances={balancesQ.data?.balances ?? []}
          onSelect={(c): void => {
            void navigate({ to: '/vault', search: { currency: c } });
          }}
        />
      )}
    </main>
  );
};

export interface VaultSearch {
  readonly currency?: string;
}

export const vaultRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/vault',
  component: VaultPage,
  validateSearch: (s: Record<string, unknown>): VaultSearch =>
    typeof s.currency === 'string' ? { currency: s.currency } : {},
});
